import { useState, useMemo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SelectSearchItem {
  id: string;
  // Utilisation d'un Record au lieu d'un index signature pour plus de flexibilité
}

// Interface générique qui étend SelectSearchItem
export interface SelectSearchProps<T extends { id: string }> {
  // État et données
  items: T[];
  selectedItems?: T[]; // Pour sélection multiple
  selectedItem?: T; // Pour sélection simple
  isLoading?: boolean;

  // Callbacks
  onItemSelect: (item: T) => void;
  onCreateNew?: () => void;
  onSearch?: (searchTerm: string) => void;

  // Configuration du filtrage
  filterFunction?: (items: T[], searchTerm: string) => T[];

  // Rendu personnalisé
  renderItem: (item: T, isSelected?: boolean) => ReactNode;
  renderTrigger?: () => ReactNode;

  // Textes configurables
  placeholder?: string;
  createNewText?: string;
  emptyMessage?: string;
  loadingMessage?: string;

  // Options d'apparence
  width?: string;
  popoverAlign?: "start" | "center" | "end";
  popoverSide?: "top" | "bottom" | "left" | "right";
  showBorder?: boolean;

  // État du popover (contrôlé ou non)
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Options de comportement
  closeOnSelect?: boolean;
  showCreateButton?: boolean;
  isMultiSelect?: boolean;
  modal?: boolean;
}

export function SelectSearch<T extends { id: string }>({
  items,
  selectedItems,
  selectedItem,
  isLoading = false,
  onItemSelect,
  onCreateNew,
  onSearch,
  filterFunction,
  renderItem,
  renderTrigger,
  placeholder = "Rechercher...",
  createNewText = "Créer nouveau",
  emptyMessage = "Aucun élément trouvé",
  loadingMessage = "Chargement...",
  width = "w-[280px]",
  popoverAlign = "start",
  popoverSide = "bottom",
  showBorder = true,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  closeOnSelect = true,
  showCreateButton = false,
  isMultiSelect = false,
  modal = false,
}: SelectSearchProps<T>) {
  // État local pour le popover si pas contrôlé de l'extérieur
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Flag pour désactiver l'animation quand on ferme après sélection (évite le "jump" visuel)
  const [skipCloseAnimation, setSkipCloseAnimation] = useState(false);

  // Utiliser l'état contrôlé ou interne
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // Filtrage des items
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;

    if (filterFunction) {
      return filterFunction(items, searchTerm);
    }

    // Filtrage par défaut (recherche dans toutes les propriétés string)
    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      return Object.values(item).some((value) => typeof value === "string" && value.toLowerCase().includes(term));
    });
  }, [items, searchTerm, filterFunction]);

  // Gérer la sélection
  const handleSelect = (item: T) => {
    // Fermer le popover AVANT d'appeler le callback si closeOnSelect est activé
    // Désactiver l'animation pour éviter le "jump" pendant la transition de fermeture
    if (closeOnSelect) {
      setSkipCloseAnimation(true);
      setOpen(false);
    }
    onItemSelect(item);
  };

  // Gérer la création
  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
    }
    setOpen(false);
  };

  // Gérer la recherche
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  // Vérifier si un item est sélectionné
  const isItemSelected = (item: T): boolean => {
    if (isMultiSelect && selectedItems) {
      return selectedItems.some((selected) => selected.id === item.id);
    }
    if (!isMultiSelect && selectedItem) {
      return selectedItem.id === item.id;
    }
    return false;
  };

  // Gérer l'ouverture/fermeture du popover
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setTimeout(() => setSearchTerm(""), 250);
      // Également informer le parent pour qu'il réinitialise son état de recherche
      if (onSearch) {
        onSearch("");
      }
      // Réinitialiser le flag après la fermeture
      setTimeout(() => setSkipCloseAnimation(false), 0);
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          renderTrigger()
        ) : (
          <Button variant="outline" className={cn("justify-start", width)}>
            <Plus className="mr-2 h-4 w-4" />
            {placeholder}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side={popoverSide}
        align={popoverAlign}
        className={cn(width, "p-0", skipCloseAnimation && "data-[state=closed]:animate-none data-[state=closed]:duration-0")}
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} className="h-9" value={searchTerm} onValueChange={handleSearch} />
          <CommandList>
            {/* Bouton de création si activé */}
            {showCreateButton && onCreateNew && (
              <>
                <CommandItem value="__create_new__" onSelect={handleCreateNew} className="m-1 flex cursor-pointer items-center justify-between">
                  {createNewText}
                  <Plus className="ml-2 h-4 w-4" />
                </CommandItem>
                {showBorder && <div className="border-t" />}
              </>
            )}

            {/* Messages d'état */}
            {isLoading && <div className="px-3 py-3 text-sm text-muted-foreground">{loadingMessage}</div>}

            {!isLoading && filteredItems.length === 0 && <div className="px-3 py-3 text-center text-sm text-muted-foreground">{emptyMessage}</div>}

            {/* Liste des items */}
            {filteredItems.length > 0 && (
              <CommandGroup className="p-0">
                {filteredItems.map((item) => {
                  const isSelected = isItemSelected(item);
                  return (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => handleSelect(item)}
                      className={cn("m-1 cursor-pointer", isSelected && isMultiSelect && "font-bold")}
                    >
                      <div className="flex w-full items-center justify-between">
                        <div className="flex-1">{renderItem(item, isSelected)}</div>
                        {isSelected && isMultiSelect && <Check className="ml-2 h-4 w-4 shrink-0" />}
                        {isSelected && !isMultiSelect && <Check className="ml-2 h-4 w-4 shrink-0 opacity-100" />}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
